import { useNavigate } from 'react-router';
import { byCategory, presetRange } from '@tahi/core';
import type { TileSize } from '../../../tiles/registry';
import { HBarList } from '../../../ui/charts';
import { Chip } from '../../../ui';
import { copy } from '../copy';
import { drillHref } from '../drill';
import { categoryColor, categoryName, spokenPeso } from '../format';
import { useCategories, useEntries, useToday, useWeekStart } from '../hooks';
import { LABEL } from '../styles';

export interface TopCategoriesConfig {
  period?: 'week' | 'month';
}

export function TopCategoriesTile({
  size,
  config,
}: {
  size: TileSize;
  config: TopCategoriesConfig;
}) {
  const navigate = useNavigate();
  const today = useToday();
  const period = config?.period ?? 'month';
  const range = presetRange(period, today, useWeekStart());
  const entries = useEntries(range) ?? [];
  const categories = useCategories() ?? [];
  const byId = new Map(categories.map((c) => [c.id, c]));
  const order = categories.map((c) => c.id);
  const buckets = byCategory(entries, range)
    .filter((b) => b.totalMinor > 0)
    .slice(0, size === 'tall' ? 6 : 3);
  const periodText = period === 'month' ? copy.thisMonth : copy.thisWeek;
  const spoken = buckets
    .map((b) => `${categoryName(b.key, byId)} ${spokenPeso(b.totalMinor)}`)
    .join(', ');

  return (
    <div
      role="group"
      className="flex flex-col gap-1"
      aria-label={`${copy.topCategories}, ${periodText}: ${spoken || copy.nothingYet}`}
    >
      <p className={LABEL}>{periodText}</p>
      {buckets.length === 0 ? (
        <p className="mt-2 text-sm text-muted">{copy.nothingYet}</p>
      ) : (
        <div className="mt-2">
          <HBarList
            ariaLabel={copy.topCategories}
            stacked={size === 'tall'}
            onSelect={(key) => navigate(drillHref({ range, dim: 'category', key }))}
            items={buckets.map((b) => ({
              key: b.key,
              label: categoryName(b.key, byId),
              valueMinor: b.totalMinor,
              color: categoryColor(b.key, order),
            }))}
          />
        </div>
      )}
    </div>
  );
}

export function TopCategoriesSettings({
  config,
  onChange,
}: {
  config: TopCategoriesConfig;
  onChange(c: TopCategoriesConfig): void;
}) {
  const period = config?.period ?? 'month';
  return (
    <div role="group" aria-label="Period" className="flex gap-2">
      <Chip
        selected={period === 'week'}
        aria-pressed={period === 'week'}
        onClick={() => onChange({ period: 'week' })}
      >
        {copy.thisWeek}
      </Chip>
      <Chip
        selected={period === 'month'}
        aria-pressed={period === 'month'}
        onClick={() => onChange({ period: 'month' })}
      >
        {copy.thisMonth}
      </Chip>
    </div>
  );
}
